import React from 'react';
import useAsrSettings from '../../../useSession/useAsrSettings';

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

function createSpeechRecognition() {
  try {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.continuous = true;
    return recognition;
  } catch (err) {
    console.error('SpeechRecognition: An error has occurred creating the speech recognition instance', err);
    return null;
  }
}

export default function useSpeechRecognition(language?: string) {
  const inProgress = !!language;
  const speechRecognition = React.useRef<any | null>(createSpeechRecognition());
  const { enableAlternativeAsrApi: settingsEnabled } = useAsrSettings();
  const [lastTranscript, setLastTranscript] = React.useState<{text: string, type: 'transcript'| 'text'} | null>(null);

  const enabled = React.useMemo(() => inProgress && settingsEnabled, [inProgress, settingsEnabled]);

  function startRecognition() {
    try {
      speechRecognition.current?.start();
    } catch (err) {
      // TODO: understand why every component is mounted twice
      if (err.message !== "Failed to execute 'start' on 'SpeechRecognition': recognition has already started.") {
        console.error('SpeechRecognition: An error has occurred starting the speech recognition', err);
      }
    }
  }

  const sendRequest = React.useCallback((transcript: string, isFinal: boolean) => {
    const trimmedTranscript = transcript.split(' ').slice(-5).join(' ');
    if (isFinal) {
      setLastTranscript({ text: trimmedTranscript, type: 'transcript' });
    } else {
      setLastTranscript({ text: trimmedTranscript, type: 'text' });
    }
  }, []);

  React.useEffect(() => {
    if (enabled && speechRecognition.current) {
      speechRecognition.current.lang = language;
      speechRecognition.current.onerror = (err) => {
        if (err.error !== 'no-speech') {
          console.error('SpeechRecognition: An error has occurred', err.error);
        }
        startRecognition();
      };

      speechRecognition.current.onnomatch = (event) => {
        console.error('SpeechRecognition: Speech not recognized', event.error);
      };

      speechRecognition.current.onend = () => {
        if (enabled) {
          startRecognition();
        }
      };

      startRecognition();
      return () => {
        if (speechRecognition.current) {
          speechRecognition.current.onerror = undefined;
          speechRecognition.current.onnomatch = undefined;
          speechRecognition.current.onend = undefined;
        }
        speechRecognition.current?.stop();
      };
    }
    return undefined;
  }, [language, enabled]);

  React.useEffect(() => {
    if (enabled && speechRecognition.current) {
      speechRecognition.current.onresult = (event) => {
        const lastText = event.results[event.results.length - 1][0].transcript;
        const { isFinal } = event.results[event.results.length - 1];
        sendRequest(lastText, isFinal);
      };
    }
  }, [enabled, sendRequest]);

  return lastTranscript;
}
